import { IcoPencilSquare, IcoCheckCircle, IcoExclamation, IcoRefresh } from '@components/icons';
import { useIntl } from 'shared/intl/use-intl';

type UploadStatus = 'pending' | 'editing' | 'uploading' | 'success' | 'failed';

type ImagePreviewProps = {
  file: File;
  previewUrl: string;
  status: UploadStatus;
  progress: number;
  onEdit: () => void;
  onRetry: () => void;
  disabled: boolean;
};

export function ImagePreview({
  file,
  previewUrl,
  status,
  progress,
  onEdit,
  onRetry,
  disabled,
}: ImagePreviewProps) {
  const intl = useIntl();

  return (
    <div>
      <div class="relative group">
        <button
          type="button"
          onClick={onEdit}
          disabled={disabled || status !== 'pending'}
          title={status === 'pending' ? intl('Edit image') : ''}
          class="relative block w-full aspect-square bg-gray-100 rounded-lg overflow-hidden focus:outline-none disabled:cursor-default"
        >
          <img src={previewUrl} alt={file.name} class="w-full h-full object-cover" />

          {status === 'uploading' && (
            <div class="absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-white p-2">
              <div class="w-full bg-gray-600 rounded-full h-1.5">
                <div class="bg-white rounded-full h-1.5" style={{ width: `${progress}%` }}></div>
              </div>
              <span class="text-xs font-medium mt-1">{Math.round(progress)}%</span>
            </div>
          )}

          {status === 'success' && (
            <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
              <IcoCheckCircle class="size-10 text-green-600 drop-shadow-lg" />
            </div>
          )}

          {status === 'failed' && (
            <div class="absolute inset-0 flex flex-col items-center justify-center gap-2">
              <IcoExclamation class="size-8 text-red-600 drop-shadow-lg" />
              <button
                type="button"
                class="bg-white text-red-700 text-xs font-bold py-1 px-3 rounded-full flex items-center gap-1 shadow-md hover:bg-gray-100"
                onClick={(e) => {
                  e.stopPropagation();
                  onRetry();
                }}
                title={intl('Retry Upload')}
              >
                <IcoRefresh class="size-3" />
                {intl('Retry')}
              </button>
            </div>
          )}

          {status === 'pending' && (
            <div class="absolute top-2 right-2 bg-white/90 p-1.5 rounded-full shadow-sm opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              <IcoPencilSquare class="size-4 text-gray-700" />
            </div>
          )}
        </button>
      </div>
      <div class="mt-2 text-xs text-gray-600 truncate" title={file.name}>
        {file.name}
      </div>
    </div>
  );
}
